// pages/updates/news-edit.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Grid, TextField, Typography, Button, Card, CardMedia, IconButton,
  FormHelperText, Checkbox, FormControlLabel, CircularProgress,
  Box
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AddPhotoAlternate, Delete } from "@mui/icons-material";
import dayjs from "dayjs";
import { DefaultPage } from "../../../layouts/default-page";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../../utils/API";
import { useUpdateNewsMutation } from "../../../services/updates.query";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Notify } from "../../../layouts/notify";

const MAX_IMAGES = 4;

const schema = Yup.object({
  title: Yup.string().required("Title is required"),
  content: Yup.string().required("Content is required"),
  date: Yup.date().required("Date is required"),
  // custom check: existing kept + new ≥ 1 (handled in submit guard too)
});

export default function EditNewsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState<any>(null);
  const [imagesToKeep, setImagesToKeep] = useState<any[]>([]); // existing IMAGE attachments you keep
  const [newImages, setNewImages] = useState<File[]>([]);
  const [link, setLink] = useState<{ id?: string; alias: string; url: string } | null>(null);

  const [updateNews, { isLoading: saving }] = useUpdateNewsMutation();

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("news")
        .select("*, attachments:news_attachments(*)")
        .eq("id", id)
        .single();
      setRow(data);
      const imgs = (data?.attachments || []).filter((a: any) => a.type === "IMAGE");
      setImagesToKeep(imgs); // default: keep all
      const linkAtt = (data?.attachments || []).find((a: any) => a.type === "FILE");
      setLink(linkAtt ? { id: linkAtt.id, alias: linkAtt.alias || "", url: linkAtt.file_url } : null);
      setLoading(false);
    })();
  }, [id]);

  if (loading) return <DefaultPage title="Edit News"><Container><CircularProgress /></Container></DefaultPage>;

  const handleRemoveExistingImage = (attId: string) => {
    setImagesToKeep(prev => prev.filter(x => x.id !== attId));
  };
  const handleAddNewImages = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    const total = imagesToKeep.length + newImages.length + arr.length;
    if (total > MAX_IMAGES) {
      Notify("error", `Maximum of ${MAX_IMAGES} images allowed`);
      return;
    }
    setNewImages(prev => [...prev, ...arr]);
  };
  const removeNewImage = (idx: number) => {
    setNewImages(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <DefaultPage title="Edit News">
      <Container maxWidth="md">
        <Formik
          validationSchema={schema}
          initialValues={{
            title: row.title || "",
            content: row.content || "",
            date: dayjs(row.date).toDate(),
            hasLink: !!link,
            linkAlias: link?.alias || "",
            linkUrl: link?.url || "",
          }}
          onSubmit={async (v) => {
            // Guard: ensure ≥1 image
            const finalCount = imagesToKeep.length + newImages.length;
            if (finalCount < 1) {
              Notify("error", "At least one image is required");
              return;
            }

            await updateNews({
              id: row.id,
              title: v.title,
              content: v.content,
              date: v.date,
              keepImageIds: imagesToKeep.map(x => x.id),
              newImages,
              linkAttachment: v.hasLink ? { id: link?.id, alias: v.linkAlias, url: v.linkUrl } : null,
            }).unwrap();

            Notify("success", "News updated");
            navigate("/newsupdates/news");
          }}
        >
          {({ values, setFieldValue, touched, errors }) => (
            <Form noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Field as={TextField} name="title" label="Title" fullWidth
                    error={touched.title && !!errors.title}
                    helperText={<ErrorMessage name="title" />} />
                </Grid>

                <Grid item xs={12}>
                  <Field as={TextField} name="content" label="Content" fullWidth multiline rows={6}
                    error={touched.content && !!errors.content}
                    helperText={<ErrorMessage name="content" />} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Date"
                    value={dayjs(values.date)}
                    onChange={(d) => setFieldValue("date", d?.toDate())}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: touched.date && !!errors.date,
                        helperText: touched.date && (errors.date as any),
                      },
                    }}
                  />
                </Grid>

                {/* Existing Images (removable) */}
                <Grid item xs={12}>
                  <Typography variant="h6">
                    Images (keep or add, max {MAX_IMAGES})
                  </Typography>

                  {/* 👉 Note */}
                  <Typography variant="body2" color="red" sx={{ mt: 0.5 }}>
                    Note: The first image in the list is treated as the <strong>MAIN IMAGE</strong>.
                    When you add new images, the earliest one added becomes the main image if there
                    are no existing images kept.
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {imagesToKeep.map((img, idx) => {
                      const isMain = idx === 0; // first of kept images = main (if any kept)
                      return (
                        <Grid item xs={6} sm={3} key={img.id}>
                          <Card sx={{ position: "relative", borderRadius: .5 }}>
                            <CardMedia component="img" height="140" image={img.file_url} />
                            {isMain && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  left: 6,
                                  px: 1,
                                  py: 0.25,
                                  bgcolor: "rgba(0,0,0,0.65)",
                                  color: "#fff",
                                  fontSize: 12,
                                  borderRadius: 1,
                                }}
                              >
                                MAIN
                              </Box>
                            )}
                            <IconButton
                              onClick={() => handleRemoveExistingImage(img.id)}
                              color="error"
                              sx={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                                bgcolor: "rgba(255,255,255,.7)",
                                borderRadius: 1.5,
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Card>
                        </Grid>
                      );
                    })}

                    {newImages.map((file, i) => {
                      // If there are no kept images, the very first new image is MAIN
                      const isMain = imagesToKeep.length === 0 && i === 0;
                      return (
                        <Grid item xs={6} sm={3} key={`new-${i}`}>
                          <Card sx={{ position: "relative", borderRadius: .5 }}>
                            <CardMedia
                              component="img"
                              height="140"
                              image={URL.createObjectURL(file)}
                            />
                            {isMain && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  left: 6,
                                  px: 1,
                                  py: 0.25,
                                  bgcolor: "rgba(0,0,0,0.65)",
                                  color: "#fff",
                                  fontSize: 12,
                                  borderRadius: 1,
                                }}
                              >
                                MAIN
                              </Box>
                            )}
                            <IconButton
                              onClick={() => removeNewImage(i)}
                              color="error"
                              sx={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                                bgcolor: "rgba(255,255,255,.7)",
                                borderRadius: 1.5,
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<AddPhotoAlternate />}
                    sx={{ mt: 1 }}
                    disabled={(imagesToKeep.length + newImages.length) >= MAX_IMAGES}
                  >
                    Add Images
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      multiple
                      onChange={(e) => handleAddNewImages(e.target.files)}
                    />
                  </Button>

                  {(imagesToKeep.length + newImages.length) === 0 && (
                    <FormHelperText error>
                      At least 1 image is required.
                    </FormHelperText>
                  )}
                </Grid>


                {/* Link toggle */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.hasLink}
                        onChange={(e) => setFieldValue("hasLink", e.target.checked)}
                      />
                    }
                    label="Add Link Attachment"
                  />
                </Grid>

                {values.hasLink && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Field as={TextField} name="linkAlias" label="Display Text" fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field as={TextField} name="linkUrl" label="Link URL" fullWidth />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Button type="submit" variant="contained" color="success" disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Container>
    </DefaultPage>
  );
}
